import React, { useState } from 'react';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import Modal from '../ui/Modal';
import BrandSelector from './BrandSelector';
import {
  TagIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

/**
 * BulkBrandAssignment Component
 * 
 * Modal for assigning a brand to multiple products at once
 */
const BulkBrandAssignment = ({ isOpen, onClose, selectedProducts, onAssignmentComplete }) => {
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProducts || selectedProducts.length === 0) {
      toast.error('לא נבחרו מוצרים');
      return;
    }

    setLoading(true);

    try {
      const productIds = selectedProducts.map(p => p.id);
      
      const response = await axios.patch('/api/products/bulk/assign-brand', {
        product_ids: productIds,
        brand_id: selectedBrandId
      });

      toast.success(response.data.message || `${productIds.length} מוצרים שויכו בהצלחה`);
      
      if (onAssignmentComplete) {
        onAssignmentComplete();
      }
      
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשיוך מוצרים לברנד');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedBrandId(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="שיוך מוצרים למותג"
      size="md"
      preventClose={loading}
    >
      <form onSubmit={handleSubmit} className="space-y-6" dir="rtl">
        {/* Selected Products Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircleIcon className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-blue-900">
              {selectedProducts?.length || 0} מוצרים נבחרו
            </h3>
          </div>
          
          <div className="max-h-32 overflow-y-auto">
            <ul className="text-sm text-blue-700 space-y-1">
              {selectedProducts?.slice(0, 5).map((product, index) => (
                <li key={product.id} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span className="truncate">{product.name}</span>
                </li>
              ))}
              {selectedProducts?.length > 5 && (
                <li className="text-blue-600 font-medium">
                  + עוד {selectedProducts.length - 5} מוצרים...
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Brand Selector */}
        <BrandSelector
          value={selectedBrandId}
          onChange={setSelectedBrandId}
          allowCreate={false}
        />

        {/* Info Message */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>שים לב:</strong> פעולה זו תשנה את המותג של כל המוצרים הנבחרים.
            אם תבחר "ללא מותג", המוצרים לא יהיו משויכים לאף מותג.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            ביטול
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            <TagIcon className="h-5 w-5" />
            <span>שייך למותג</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default BulkBrandAssignment;
